// External dependencies
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Route, Redirect, Switch } from 'react-router';
import { ConnectedRouter } from 'react-router-redux';
import PropTypes from 'prop-types';
import {
  currentUserJurisdictionAndStatusSelector,
  currentUserAndStatusSelector,
} from './store/selectors';
import debugMod from 'debug';

import {
  readCurrentUser,
  readJurisdiction,
  readCurrentUserAndJurisdiction,
} from './store/apiActions';

// Components
import Loader from './components/Loader';
import { NetworkErrorModal } from './components/Modals';
import FooterBar from './components/FooterBar';
import Banner from './components/Banner';

// Containers
import FirmContainer from './containers/FirmContainer';
import SuperUserScopeSearchContainer from './containers/SuperUserScopeSearchContainer';
import HeaderBarContainer from './containers/HeaderBarContainer';
import LoginScreenContainer from './containers/LoginScreenContainer';
import PrivateRouteContainer from './containers/PrivateRouteContainer';
import AlertsContainer from './containers/AlertsContainer';
import ControlOwnersContainer from './containers/ControlOwnersContainer';

import { messages } from './intl';

// Stylesheets
import './styles/App.css';

// eslint-disable-next-line
const debug = debugMod('roster:RosterApp');

// English fallbacks because `npm run translate` drops keys missing from translation/label_ids.json
const BANNER_MESSAGE =
  messages['banner.rstr_end_of_life'] ||
  'On December 10, 2026, the Roster web application and RSTR <GO> will reach end of life and retire. For more information, run HELP RSTR <GO> on the Bloomberg Terminal.';
const BANNER_DISMISS_LABEL = messages['banner.dismiss'] || 'Dismiss';
// Keyed to the message text so a new message shows even if an earlier one was dismissed
export const BANNER_DISMISSED_KEY = `roster.bannerDismissed:${BANNER_MESSAGE}`;

function readBannerDismissed() {
  try {
    return window.sessionStorage.getItem(BANNER_DISMISSED_KEY) === 'true';
  } catch (e) {
    // sessionStorage can be unavailable (e.g. blocked by browser privacy settings)
    return false;
  }
}

function writeBannerDismissed() {
  try {
    window.sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true');
  } catch (e) {
    // Banner still hides for the current page view via component state
  }
}

const mapStateToProps = (state) => {
  const {
    JwtAuth: { isInitialized, isAuthenticated, isServerError },
  } = state;

  const [currentUserAttrs, currentUserStatus] =
    currentUserAndStatusSelector(state);
  const [currentUserJurisdiction, jurisdictionStatus] =
    currentUserJurisdictionAndStatusSelector(state);
  const { firmId: currentUserFirm } = { ...currentUserAttrs };
  const {
    isInternalUser: isCurrentUserInternal,
    isSuperUser: isCurrentUserSuperUser,
  } = { ...currentUserJurisdiction };

  const isLoaded =
    isInitialized && // Non-0 Status received from server AND Error received from server
    (isServerError || // OR User and Jurisdiction requests complete
      !(currentUserStatus.pending && jurisdictionStatus.pending));
  return {
    currentUserFirm,
    isCurrentUserSuperUser,
    isCurrentUserInternal,
    isLoaded,
    isAuthenticated,
    isServerError,
  };
};

const mapDispatchToProps = {
  readCurrentUser,
  readJurisdiction,
  readCurrentUserAndJurisdiction,
};

export class RosterApp extends Component {
  state = {
    isBannerDismissed: readBannerDismissed(),
  };

  componentDidMount() {
    this.props.readCurrentUserAndJurisdiction();
  }

  dismissBanner = () => {
    writeBannerDismissed();
    this.setState({ isBannerDismissed: true });
  };

  render() {
    const {
      currentUserFirm,
      isCurrentUserSuperUser,
      isCurrentUserInternal,
      isLoaded,
      isAuthenticated,
      isServerError,
      history,
    } = this.props;

    return isLoaded ? (
      <div className="bb-asset-vault">
        <ConnectedRouter history={history}>
          <div className="appWrapper">
            <Route component={HeaderBarContainer} />
            {
              // Shown on whichever page a logged-in user opens until dismissed
              isAuthenticated && !this.state.isBannerDismissed && (
                <Banner
                  message={BANNER_MESSAGE}
                  dismissLabel={BANNER_DISMISS_LABEL}
                  onDismiss={this.dismissBanner}
                />
              )
            }
            <AlertsContainer />
            <Switch>
              <Route path="/login" component={LoginScreenContainer} />
              {
                // It seems like a good idea to wrap this around a <div> or <React.Fragment>
                // BUT somehow doing so messes up react router redirect
                isCurrentUserInternal && isCurrentUserSuperUser ? (
                  <PrivateRouteContainer
                    exact
                    path="/"
                    component={SuperUserScopeSearchContainer}
                  />
                ) : (
                  <PrivateRouteContainer
                    exact
                    path="/"
                    component={() => (
                      <Redirect to={`/firm/${currentUserFirm}/roster`} />
                    )}
                  />
                )
              }
              {isCurrentUserSuperUser && (
                <PrivateRouteContainer
                  path="/controlOwners"
                  component={ControlOwnersContainer}
                />
              )}
              {!isAuthenticated ||
              (isCurrentUserInternal && isCurrentUserSuperUser) ? (
                <PrivateRouteContainer
                  path="/firm/:firmId([1-9][0-9]*)/:firmTab(roster)/:subFirmTab(admins|monitoredEmployees|jurisdiction)?"
                  component={FirmContainer}
                />
              ) : (
                <PrivateRouteContainer
                  path={`/firm/:firmId(${currentUserFirm})/:firmTab(roster)/:subFirmTab(admins|monitoredEmployees|jurisdiction)?`}
                  component={FirmContainer}
                />
              )}
              <Redirect to="/" />
            </Switch>
            {isServerError && <NetworkErrorModal />}
            <FooterBar />
          </div>
        </ConnectedRouter>
      </div>
    ) : (
      <div className="bb-asset-vault">
        <div className="appLoader row">
          <Loader />
        </div>
      </div>
    );
  }
}

RosterApp.propTypes = {
  currentUserFirm: PropTypes.number,
  isCurrentUserSuperUser: PropTypes.bool,
  isCurrentUserInternal: PropTypes.bool,
  isLoaded: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool,
  isServerError: PropTypes.bool.isRequired,
  readCurrentUserAndJurisdiction: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(RosterApp);
